// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The cabinet in your hand (M3-42).
//
// **This product had haptics and none of them reached a phone.** `src/xr/haptics.ts` drives a
// Quest controller's actuators — a flipper click, a grab, a shove — and a player on the device
// most people will actually hold got a flipper with no click and a bumper with no thump. On a
// machine whose entire pitch is *"the phone is a first-class place to play"*, that is the largest
// single gap between what the simulation knows and what the player feels.
//
// WHAT THIS IS ALLOWED TO BE
// --------------------------
// `navigator.vibrate` and nothing else. There is no per-actuator control, no amplitude and no
// waveform on the web — the whole API is "buzz for N milliseconds", so the vocabulary here is
// **durations chosen to be distinguishable from each other**, not an attempt at fidelity. Two
// buzzes a player cannot tell apart are worse than one, because they teach that the buzzing means
// nothing in particular.
//
// **Android only, in practice, and that is not a reason to skip it.** iOS Safari does not
// implement `navigator.vibrate` and shows no sign of doing so; `supported()` answers honestly and
// the Settings control hides itself rather than offering an iPhone a switch that does nothing.
// Roughly half the phones that will ever open this get the feature and the other half are exactly
// as they are today.
//
// WHY IT IS RATE-LIMITED
// ----------------------
// **A pinball machine produces thousands of contacts a second and a motor cannot answer them.**
// Calling `vibrate` on every contact does not produce a rich texture; it produces one continuous
// hum, because each call *replaces* the last and the motor never gets to stop. So the strongest
// impact in a frame wins, there is a floor on how often the motor may be asked, and a soft roll
// along a rail is silent by design — the same reasoning as `CONTACT_REPORT_SPEED` discarding
// everything below 8 cm/s in the feed this reads.

/** What this needs of a navigator. A real one satisfies it. */
export interface VibrateHost {
  vibrate: (pattern: number | number[]) => boolean
}

/** The browser's, or nothing where there is none. */
export function browserVibrate(): VibrateHost | undefined {
  const nav = (globalThis as { navigator?: { vibrate?: (p: number | number[]) => boolean } })
    .navigator
  return typeof nav?.vibrate === 'function' ? (nav as VibrateHost) : undefined
}

/** Is there a motor here at all? What Settings asks before offering the control. */
export function hapticsSupported(host: VibrateHost | undefined = browserVibrate()): boolean {
  return host !== undefined
}

/**
 * The vocabulary, in milliseconds.
 *
 * Each of these is a thing a player can be taught, and the durations are far enough apart to be
 * told apart through a phone case:
 *
 *   * `flipper` — 8 ms. The shortest useful pulse: a click under the thumb that confirms the bat
 *     went up, which is the one piece of feedback a touch control cannot give by itself. A physical
 *     button tells your finger it has been pressed; a rectangle of glass does not.
 *   * `soft` / `hard` — a ball hitting something, scaled by how fast it hit. Two steps rather than
 *     a continuum, because a motor spinning up and down cannot express a continuum in 20 ms.
 *   * `drain` — a double tap. The one *bad* thing that happens, and the only pattern in the set,
 *     so it is unmistakable without being long enough to feel like a punishment.
 *   * `tilt` — long and flat. The machine refusing, which is a different kind of event from the
 *     ball hitting something, and it should feel like a buzzer rather than a knock.
 *   * `launch` — the plunger releasing.
 */
export const PULSE = {
  flipper: 8,
  soft: 12,
  hard: 22,
  drain: [18, 60, 30] as number[],
  tilt: 120,
  launch: 26,
} as const

export type PulseName = keyof typeof PULSE

/**
 * The slowest contact worth a buzz, in metres per second.
 *
 * Above `CONTACT_REPORT_SPEED`, which is the feed's own floor at 8 cm/s. A ball rolling down a
 * rail or settling in a saucer clears that and should still be silent to the hand: the feed's job
 * is to report everything the presentation layer *may* want, and this one wants only the knocks.
 */
export const HAPTIC_MIN_SPEED = 0.9

/** …and the speed at which a contact is a hard one rather than a soft one. */
export const HAPTIC_HARD_SPEED = 3.2

/**
 * The shortest gap between two buzzes, milliseconds.
 *
 * **The number that stops the motor becoming a hum.** Each `vibrate` call cancels the previous
 * one, so a multiball against a slingshot bank would otherwise be a single unbroken vibration for
 * as long as it lasted — which reads as a broken phone, not as a busy machine. 60 ms lets a
 * genuine rally through of about sixteen distinct knocks a second and refuses the rest.
 */
export const HAPTIC_MIN_GAP_MS = 60

/**
 * Sends pulses to the device, if there is one, if the player wants them, and not too often.
 *
 * Holds its own clock source so a test can state exactly what was sent and when, rather than
 * racing one. Not in `core/` and therefore allowed a clock at all — nothing here is simulated and
 * nothing here may ever affect the simulation, which is the line this class must not cross: a
 * haptic is a *consequence* of a step, and a step that read one would be a step whose outcome
 * depended on which phone ran it.
 */
export class TouchHaptics {
  private lastAt = Number.NEGATIVE_INFINITY
  private enabled = true

  constructor(
    private readonly host: VibrateHost | undefined = browserVibrate(),
    private readonly now: () => number = () => Date.now(),
  ) {}

  /** The player's setting. Off means silent; there is no partial. */
  setEnabled(on: boolean): void {
    this.enabled = on
    if (!on) this.stop()
  }

  get on(): boolean {
    return this.enabled && this.host !== undefined
  }

  /** Send one of the named pulses, subject to the gap. Answers whether anything was sent. */
  pulse(name: PulseName): boolean {
    if (!this.on) return false
    const at = this.now()
    if (at - this.lastAt < HAPTIC_MIN_GAP_MS) return false
    this.lastAt = at
    try {
      this.host?.vibrate(PULSE[name] as number | number[])
      return true
    } catch {
      // Some Android builds throw rather than return false when the user has vibration off at the
      // system level. That is the platform answering the question and not a fault to report.
      return false
    }
  }

  /**
   * The strongest contact of a frame, as a pulse.
   *
   * **One per frame, not one per contact.** A frame can carry hundreds; the hand can feel one.
   * Taking the strongest rather than the first is what makes the buzz mean "the biggest thing that
   * just happened" instead of "something happened at some point".
   */
  contact(speed: number): boolean {
    if (speed < HAPTIC_MIN_SPEED) return false
    return this.pulse(speed >= HAPTIC_HARD_SPEED ? 'hard' : 'soft')
  }

  /** Cancel anything running. Called when the machine stops or the tab is hidden. */
  stop(): void {
    try {
      this.host?.vibrate(0)
    } catch {
      // As above.
    }
  }
}
